import Config from '@/config'
import { ApiRequestError } from '@/errors/ApiRequestError'
import { logger } from '@/lib/logger'
import type { ApisauceInstance } from 'apisauce'
import { create } from 'apisauce'
import { authRequestInterceptor } from './interceptors/auth'
import type {
	AppointmentResultReportResponse,
	BookVisitRequest,
	BookedVisit,
	CheckInBlock,
	UpcomingPatientAppointment,
	UpcomingVisit,
	WeeklyAvailability,
} from './types/ProviderApi'

/**
 * The scheduling refusals worth telling apart.
 *
 * Two of them are 409s, a time taken and a booking already under way, so the code in the body
 * decides what the patient is told, not the status.
 */
const scheduleError = (
	response: { status?: number; problem?: string | null; data?: unknown },
	fallback: string
) => {
	const body =
		response.data && typeof response.data === 'object'
			? (response.data as { message?: unknown; code?: unknown })
			: {}
	const message =
		typeof body.message === 'string' && body.message.trim()
			? body.message
			: `${fallback}: ${response.problem}`
	const code = typeof body.code === 'string' ? body.code : undefined
	return new ApiRequestError(message, response.status, code)
}

export class ProviderApi {
	_apiConfig: ApisauceInstance

	constructor() {
		this._apiConfig = create({
			baseURL: Config.PROVIDER_API_URL_BASE,
			timeout: 10000,
		})

		this._apiConfig.axiosInstance.interceptors.request.use(authRequestInterceptor)
	}

	fetchUpcomingAppointment() {
		return this._apiConfig
			.get('/appointments/next-appointment')
			.then((response) => response.data as UpcomingPatientAppointment)
			.catch((error) => {
				logger.error(new Error('failed_to_get_upcoming_appointment', { cause: error }))
				return {} as UpcomingPatientAppointment
			})
	}

	/** Every upcoming visit, soonest first. Throws on failure so the home screen can say so. */
	fetchUpcomingVisits() {
		return this._apiConfig.get('/appointments/patient-upcoming').then((response) => {
			if (!response.ok) {
				throw new Error(`failed_to_get_upcoming_visits: ${response.problem}`)
			}
			return response.data as UpcomingVisit[]
		})
	}

	/** One week of openings across the studios in the patient's geo, studio by studio. */
	fetchPatientAvailability({
		weekStart,
		geoId,
		rescheduleAppointmentId,
	}: { weekStart: string; geoId?: number; rescheduleAppointmentId?: number }) {
		return this._apiConfig
			.get('/member-booking/availability', {
				weekStart,
				geoId,
				rescheduleAppointmentId,
			})
			.then((response) => {
				if (!response.ok) {
					throw scheduleError(response, 'failed_to_get_patient_availability')
				}
				return response.data as WeeklyAvailability
			})
	}

	/**
	 * Books the chosen time, and on a move releases the old visit only once this one is booked.
	 *
	 * The times are the studio's wall clock and carry no offset; sending an instant would book a
	 * different hour than the patient chose, silently.
	 */
	bookPatientVisit(request: BookVisitRequest) {
		return this._apiConfig
			.post('/member-booking/appointments', request)
			.then((response) => {
				if (!response.ok) {
					throw scheduleError(response, 'failed_to_book_visit')
				}
				return response.data as BookedVisit
			})
	}

	/**
	 * Check in, and take back the door's new state.
	 *
	 * Throws on failure rather than swallowing, unlike `confirmAppointment` beneath it: a member
	 * pressing this is standing in a lobby, and the one thing worse than "we couldn't check you
	 * in" is a button that does nothing and says nothing.
	 *
	 * The server answers with the state whatever it is — already in, too early, closed — so the
	 * only thing that reaches the catch is a call that did not complete.
	 */
	checkInAppointment(appointmentId: number): Promise<CheckInBlock> {
		return this._apiConfig
			.post<CheckInBlock>(`/appointments/${appointmentId}/check-in`)
			.then((response) => {
				if (!response.ok || !response.data) {
					throw new Error(`failed_to_check_in: ${response.problem}`)
				}
				return response.data
			})
	}

	confirmAppointment(appointmentId: number) {
		return this._apiConfig
			.post(`/appointments/${appointmentId}/confirm`)
			.catch((error) => {
				logger.error(new Error('failed_to_confirm_appointment', { cause: error }))
			})
	}

	cancelAppointment(appointmentId: number) {
		return this._apiConfig
			.post(`/appointments/${appointmentId}/patient-cancel`)
			.catch((error) => {
				logger.error(new Error('failed_to_cancel_appointment', { cause: error }))
			})
	}

	fetchLatestAppointmentResults() {
		return this._apiConfig.get('/appointment-results/latest').then((response) => {
			if (!response.ok) {
				return null
			}

			return response.data as AppointmentResultReportResponse
		})
	}
}

export const providerApi = new ProviderApi()
